import React, { useState } from "react";
import {
    Box,
    Grid,
    Paper,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Fab,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import {
    makePOSTRequestOnUserService,
    makePUTRequestOnUserService,
    makeDELETERequestOnUserServiceWithQueryParameter,
} from "../../Api/api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Download } from "@mui/icons-material";

const ACCOUNT_TYPE_CHOICES = [
    { value: "savings", label: "Savings Account" },
    { value: "current", label: "Current Account" },
];

const NRI_BANK_ACCOUNT_TYPE_CHOICES = [
    { value: "nro", label: "NRO" },
    { value: "nre", label: "NRE" },
];

const PIS_TYPE_CHOICES = [
    { value: "pis", label: "PIS" },
    { value: "non_pis", label: "Non-PIS" },
];

export const BankDetails = ({
    bank_details = [],
    fetchKycDocs,
    currentUser,
    user_type,
    setValue,
    getErrorMessage,
    isNRI,
    accountTypeState,
}: any) => {
    const toastr = require("toastr");

    const safeBankDetails = Array.isArray(bank_details) ? bank_details : [];

    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [file, setFile] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        bank_name: "",
        beneficiary_name: "",
        branch_name: "",
        account_number: "",
        ifsc_code: "",
        password: "",
        account_type: "savings",
        nri_bank_account_type: "",
        pis_type: "",
    });

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setForm((prev) => {
            const updated = { ...prev, [name]: value };
            if (name === "nri_bank_account_type" && value === "nro") {
                updated.pis_type = "";
            }
            return updated;
        });
    };

    const resetForm = () => {
        setForm({
            bank_name: "",
            beneficiary_name: "",
            branch_name: "",
            account_number: "",
            ifsc_code: "",
            password: "",
            account_type: "savings",
            nri_bank_account_type: "",
            pis_type: "",
        });
        setFile(null);
        setEditData(null);
    };

    const handleCreateOrUpdate = async () => {
        if (isNRI && form.nri_bank_account_type === "nre" && !form.pis_type) {
            toastr.error("PIS Type is required when Bank Account Type is NRE");
            return;
        }

        try {
            setLoading(true);
            const fd = new FormData();
            fd.append("user_type", user_type);
            fd.append("user_id", currentUser.uuid);
            fd.append("account_number", form.account_number);
            fd.append("ifsc_code", form.ifsc_code);
            fd.append("bank_name", form.bank_name);
            fd.append("branch_name", form.branch_name);
            fd.append("beneficiary_name", form.beneficiary_name);
            fd.append("kyc_doc_plaintext_password", form.password || "");
            fd.append("account_type", form.account_type || "savings");
            fd.append("perform_penny_drop", "false");

            if (isNRI) {
                if (form.nri_bank_account_type) {
                    fd.append(
                        "nri_bank_account_type",
                        form.nri_bank_account_type
                    );
                }
                if (form.nri_bank_account_type === "nre" && form.pis_type) {
                    fd.append("pis_type", form.pis_type);
                }
                if (file) {
                    fd.append("nri_bank_proof", file);
                }
            } else {
                if (file) {
                    fd.append("image", file);
                }
            }

            let resp;

            if (editData) {
                resp = await makePUTRequestOnUserService(
                    "/partneruser/bank_detail/update/",
                    fd
                );
            } else {
                resp = await makePOSTRequestOnUserService(
                    "/partneruser/bank_detail/create/",
                    fd
                );
            }

            if (resp.status === 200 || resp.status === 201) {
                setOpen(false);
                resetForm();
                fetchKycDocs();
                toastr.success("Saved Successfully");
                setValue((prev: number) => prev + 1);
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDefault = async (account_number: string) => {
        try {
            const resp = await makePUTRequestOnUserService(
                "/partneruser/bank_detail/update_default_bank_details/",
                {
                    user_type,
                    user_id: currentUser.uuid,
                    account_number,
                    is_default: true,
                    admin_name: JSON.parse(localStorage.getItem("loginUser")!)
                        ?.name,
                }
            );

            if (resp.status === 200) {
                toastr.success("Set as default");
                fetchKycDocs();
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        }
    };

    const handleDelete = async (account_number: string) => {
        try {
            const resp =
                await makeDELETERequestOnUserServiceWithQueryParameter(
                    `/partneruser/bank_detail/delete/${user_type}/${currentUser.uuid}/${account_number}/`
                );

            if (resp.status === 204) {
                toastr.success("Deleted");
                fetchKycDocs();
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        }
    };

    const handleEdit = (item: any) => {
        setEditData(item);
        setForm({
            bank_name: item.bank_name || "",
            beneficiary_name: item.beneficiary_name || "",
            branch_name: item.branch_name || "",
            account_number: item.account_number || "",
            ifsc_code: item.ifsc_code || "",
            password: item.kyc_doc_plaintext_password || "",
            account_type: item.account_type || "savings",
            nri_bank_account_type: item.nri_bank_account_type || "",
            pis_type: item.pis_type || "",
        });
        setFile(null);
        setOpen(true);
    };

    const handleDownload = (item: any, isNriProof: boolean = false) => {
        try {
            const imageData = isNriProof
                ? item?.nri_bank_proof_image
                : item?.image;
            const filename = isNriProof
                ? item?.nri_bank_proof_filename
                : item?.image_filename;

            if (!imageData) {
                toastr.error("No file available");
                return;
            }
            const isPDF = filename?.toLowerCase().endsWith(".pdf");
            const mimeType = isPDF ? "application/pdf" : "image/jpeg";
            const byteCharacters = atob(imageData);
            const byteNumbers = new Array(byteCharacters.length)
                .fill(0)
                .map((_, i) => byteCharacters.charCodeAt(i));
            const blob = new Blob([new Uint8Array(byteNumbers)], {
                type: mimeType,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || "document";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            toastr.error("Download failed");
        }
    };

    const getItemImageData = (item: any) => {
        if (isNRI && item.nri_bank_proof_filename) {
            return {
                image: item.nri_bank_proof_image || "",
                filename: item.nri_bank_proof_filename,
            };
        }
        return {
            image: item.image || "",
            filename: item.image_filename,
        };
    };

    return (
        <Box sx={{ position: "relative", width: "100%", pt: 5 }}>
            {safeBankDetails.length < 5 && (
                <Fab
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 10,
                        backgroundColor: "#262349",
                        color: "#fff",
                        width: 36,
                        height: 36,
                        zIndex: 10,
                        "&:hover": { backgroundColor: "#1d1a3a" },
                    }}
                    onClick={() => {
                        resetForm();
                        setOpen(true);
                    }}
                >
                    <AddIcon sx={{ fontSize: 18 }} />
                </Fab>
            )}

            {bank_details.length === 0 && (
                <Box
                    sx={{
                        height: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="h6" color="text.secondary">
                        No Bank Details Added
                    </Typography>
                </Box>
            )}

            <Grid container spacing={2}>
                {safeBankDetails.map((item: any) => {
                    const itemImage = getItemImageData(item);
                    const isItemNRI =
                        item.nri_bank_account_type === "nro" ||
                        item.nri_bank_account_type === "nre";

                    return (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={item.account_number}
                        >
                            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
                                {/* Image preview */}
                                {itemImage.image ? (
                                    itemImage.filename?.toLowerCase().endsWith(
                                        ".pdf"
                                    ) ? (
                                        (() => {
                                            const byteCharacters = atob(
                                                itemImage.image
                                            );
                                            const byteNumbers = new Array(
                                                byteCharacters.length
                                            )
                                                .fill(0)
                                                .map(
                                                    (_, i) =>
                                                        byteCharacters.charCodeAt(
                                                            i
                                                        )
                                                );
                                            const blob = new Blob(
                                                [new Uint8Array(byteNumbers)],
                                                { type: "application/pdf" }
                                            );
                                            const url =
                                                URL.createObjectURL(blob);
                                            return (
                                                <iframe
                                                    src={url}
                                                    title="PDF Preview"
                                                    style={{
                                                        width: "100%",
                                                        height: 160,
                                                        borderRadius: 8,
                                                        border: "none",
                                                    }}
                                                />
                                            );
                                        })()
                                    ) : (
                                        <img
                                            src={`data:image/jpeg;base64,${itemImage.image}`}
                                            alt="bank"
                                            style={{
                                                width: "100%",
                                                height: 160,
                                                objectFit: "cover",
                                                borderRadius: 8,
                                            }}
                                        />
                                    )
                                ) : (
                                    <Box
                                        sx={{
                                            height: 160,
                                            background: "#eee",
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        No File
                                    </Box>
                                )}

                                <Typography mt={1} fontSize={13}>
                                    Password -{" "}
                                    {item.kyc_doc_plaintext_password || "NA"}
                                </Typography>

                                <Typography
                                    mt={2}
                                    fontSize={13}
                                    color="text.secondary"
                                >
                                    {item.account_number}
                                </Typography>

                                <Typography fontSize={16} fontWeight={600}>
                                    {item.bank_name}
                                </Typography>

                                <Typography
                                    fontSize={13}
                                    color="text.secondary"
                                >
                                    {item.beneficiary_name}
                                </Typography>

                                <Typography mt={2} fontSize={13}>
                                    {item.branch_name}
                                </Typography>

                                <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                                    <Chip
                                        label={
                                            ACCOUNT_TYPE_CHOICES.find(
                                                (c) =>
                                                    c.value ===
                                                    item.account_type
                                            )?.label || item.account_type
                                        }
                                        size="small"
                                        sx={{
                                            backgroundColor: "#e0e0e0",
                                            fontWeight: 500,
                                            borderRadius: "8px",
                                        }}
                                    />
                                    {isItemNRI && (
                                        <Chip
                                            label={
                                                item.nri_bank_account_type?.toUpperCase()
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor: "#ffe0b2",
                                                fontWeight: 500,
                                                borderRadius: "8px",
                                            }}
                                        />
                                    )}
                                    {item.pis_type && (
                                        <Chip
                                            label={
                                                PIS_TYPE_CHOICES.find(
                                                    (c) =>
                                                        c.value ===
                                                        item.pis_type
                                                )?.label || item.pis_type
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor: "#e1f5fe",
                                                fontWeight: 500,
                                                borderRadius: "8px",
                                            }}
                                        />
                                    )}
                                </Box>

                                <Box
                                    mt={1}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Box>
                                        <IconButton
                                            onClick={() => handleEdit(item)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() =>
                                                handleDelete(
                                                    item.account_number
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() =>
                                                handleDownload(
                                                    item,
                                                    isNRI &&
                                                    !!item.nri_bank_proof_filename
                                                )
                                            }
                                        >
                                            <Download />
                                        </IconButton>
                                    </Box>

                                    {!item.is_default ? (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() =>
                                                handleDefault(
                                                    item.account_number
                                                )
                                            }
                                            sx={{
                                                borderColor: "#262349",
                                                color: "#262349",
                                                borderRadius: "20px",
                                                textTransform: "none",
                                                px: 2,
                                                "&:hover": {
                                                    borderColor: "#1d1a3a",
                                                    backgroundColor:
                                                        "rgba(38,35,73,0.05)",
                                                },
                                            }}
                                        >
                                            Set Default
                                        </Button>
                                    ) : (
                                        <Chip
                                            label="Default"
                                            size="small"
                                            sx={{
                                                backgroundColor: "green",
                                                color: "#fff",
                                                fontWeight: 500,
                                                borderRadius: "16px",
                                                marginTop: "7px",
                                            }}
                                        />
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {editData ? "Update Bank Details" : "Create Bank Details"}
                </DialogTitle>

                <DialogContent>
                    <TextField
                        fullWidth
                        label="Bank Name"
                        name="bank_name"
                        value={form.bank_name}
                        onChange={handleChange}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Beneficiary Name"
                        name="beneficiary_name"
                        value={form.beneficiary_name}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Branch Name"
                        name="branch_name"
                        value={form.branch_name}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Account Number"
                        name="account_number"
                        value={form.account_number}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="IFSC Code"
                        name="ifsc_code"
                        value={form.ifsc_code}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                        <InputLabel id="account-type-label">
                            Account Type
                        </InputLabel>
                        <Select
                            labelId="account-type-label"
                            name="account_type"
                            value={form.account_type}
                            onChange={handleChange}
                            label="Account Type"
                        >
                            {ACCOUNT_TYPE_CHOICES.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {isNRI && (
                        <>
                            <FormControl
                                fullWidth
                                variant="outlined"
                                sx={{ mb: 2 }}
                            >
                                <InputLabel id="nri-bank-type-label">
                                    NRI Bank Account Type
                                </InputLabel>
                                <Select
                                    labelId="nri-bank-type-label"
                                    name="nri_bank_account_type"
                                    value={form.nri_bank_account_type}
                                    onChange={handleChange}
                                    label="NRI Bank Account Type"
                                >
                                    {NRI_BANK_ACCOUNT_TYPE_CHOICES.map(
                                        (opt) => (
                                            <MenuItem
                                                key={opt.value}
                                                value={opt.value}
                                            >
                                                {opt.label}
                                            </MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>

                            {form.nri_bank_account_type === "nre" && (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    required
                                >
                                    <InputLabel id="pis-type-label">
                                        PIS Type
                                    </InputLabel>
                                    <Select
                                        labelId="pis-type-label"
                                        name="pis_type"
                                        value={form.pis_type}
                                        onChange={handleChange}
                                        label="PIS Type *"
                                    >
                                        {PIS_TYPE_CHOICES.map((opt) => (
                                            <MenuItem
                                                key={opt.value}
                                                value={opt.value}
                                            >
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </>
                    )}

                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {isNRI
                            ? "Upload NRI Bank Proof"
                            : "Upload Cancelled Cheque"}
                    </Typography>
                    <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setFile(e.target.files?.[0])}
                        style={{ marginBottom: "10px" }}
                    />

                    <Box
                        sx={{
                            height: 160,
                            border: "1px dashed #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 2,
                            background: "#fafafa",
                            mb: 2,
                        }}
                    >
                        {file ? (
                            file.type === "application/pdf" ? (
                                <iframe
                                    src={URL.createObjectURL(file)}
                                    style={{
                                        width: "100%",
                                        height: 160,
                                        border: "none",
                                    }}
                                />
                            ) : (
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt="preview"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                    }}
                                />
                            )
                        ) : editData ? (
                            (() => {
                                const editImg = getItemImageData(editData);
                                return editImg.image ? (
                                    editImg.filename?.toLowerCase().endsWith(
                                        ".pdf"
                                    ) ? (
                                        (() => {
                                            const byteCharacters = atob(
                                                editImg.image
                                            );
                                            const byteNumbers = new Array(
                                                byteCharacters.length
                                            )
                                                .fill(0)
                                                .map(
                                                    (_, i) =>
                                                        byteCharacters.charCodeAt(
                                                            i
                                                        )
                                                );
                                            const blob = new Blob(
                                                [
                                                    new Uint8Array(
                                                        byteNumbers
                                                    ),
                                                ],
                                                { type: "application/pdf" }
                                            );
                                            const url =
                                                URL.createObjectURL(blob);
                                            return (
                                                <iframe
                                                    src={url}
                                                    style={{
                                                        width: "100%",
                                                        height: 160,
                                                        border: "none",
                                                    }}
                                                />
                                            );
                                        })()
                                    ) : (
                                        <img
                                            src={`data:image/jpeg;base64,${editImg.image}`}
                                            alt="edit preview"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                            }}
                                        />
                                    )
                                ) : (
                                    <Typography>Upload Image / PDF</Typography>
                                );
                            })()
                        ) : (
                            <Typography>Upload Image / PDF</Typography>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "flex-start",
                        px: 3,
                        pb: 2,
                        gap: 1,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleCreateOrUpdate}
                        disabled={loading}
                        sx={{
                            backgroundColor: "#262349",
                            "&:hover": { backgroundColor: "#1d1a3a" },
                        }}
                    >
                        {loading ? "Saving..." : "Submit"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(false)}
                        sx={{
                            borderColor: "#262349",
                            color: "#262349",
                            "&:hover": {
                                borderColor: "#1d1a3a",
                                color: "#1d1a3a",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};